import React, { useState, useEffect } from 'react';
import {
  Form, Input, Button, Select, Card, Row, Col, Upload, Space, Tag, message
} from 'antd';
import { PlusOutlined, UploadOutlined, MinusCircleOutlined, EyeOutlined, FileOutlined } from '@ant-design/icons';
import { getSkills, imagepath } from '../api';

const { Option } = Select;
const documentTypes = [
  { value: 'passport', label: 'Passport' },
  { value: 'visa', label: 'Visa' },
  { value: 'emirates_id', label: 'Emirates ID (Front)' },
  { value: 'emirates_id_back', label: 'Emirates ID (Back)' },
  { value: 'labour_card', label: 'Labour Card' },
  { value: 'driving_licence', label: 'Driving Licence' },
  { value: 'health_insurance', label: 'Health Insurance' },
  { value: 'workmen_insurance', label: 'Workmen Insurance' },
  { value: 'other', label: 'Other' },
];
const countryList = [
  'United Arab Emirates', 'India', 'Saudi Arabia', 'Qatar', 'Oman', 'Kuwait', 'Bahrain', 'Pakistan', 'Bangladesh', 'Nepal', 'Sri Lanka', 'Philippines', 'Other'
];

const EmployeeForm = ({ initialValues, onSubmit, loading, mode = 'add', departments = [], onCancel }) => {
  const [form] = Form.useForm();
  const [imagePreview, setImagePreview] = useState(null);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    if (initialValues) {
      // Transform documents data to match form structure
      const formattedValues = { ...initialValues };

      if (formattedValues.documents && formattedValues.documents.length > 0) {
        formattedValues.documents = formattedValues.documents.map(doc => {
          if (!doc.document_file) return { ...doc, document_file: [] };
          if (Array.isArray(doc.document_file)) return doc;

          const rawPath = typeof doc.document_file === 'string' ? doc.document_file : '';
          const fileUrl = rawPath.startsWith('http') ? rawPath : `${imagepath}${rawPath}`;
          const fileName = doc.original_filename || (rawPath ? rawPath.split('/').pop() : 'document');

          return {
            ...doc,
            document_file: [{
              uid: String(doc.id || Math.random()),
              name: fileName,
              status: 'done',
              url: fileUrl,
              thumbUrl: fileUrl,
            }]
          };
        });
      }

      if (formattedValues.leaves && formattedValues.leaves.length > 0) {
        formattedValues.leaves = formattedValues.leaves.map(leave => ({
          ...leave,
          form_file: leave.leave_application_form ? [{
            uid: leave.id,
            name: leave.original_filename || leave.leave_application_form.split('/').pop(),
            status: 'done',
            url: `${imagepath}${leave.leave_application_form}`,
          }] : []
        }));
      }

      form.setFieldsValue(formattedValues);
      setImagePreview(
        formattedValues.profile_image && formattedValues.profile_image.length > 0 && formattedValues.profile_image[0].url
          ? formattedValues.profile_image[0].url
          : null
      );
    }
    // eslint-disable-next-line
  }, [initialValues]);

  useEffect(() => {
    getSkills().then(res => {
      if (res.success) setSkills(res.data);
    });
  }, []);

  const onFinish = async (values) => {
    console.log('EmployeeForm onFinish - Raw values:', values);
    console.log('Documents in values:', values.documents);

    // Process documents to ensure they have the correct structure
    const processedValues = {
      ...values,
      documents: values.documents?.flatMap(doc => {
        console.log('Processing document:', doc);
        const files = Array.isArray(doc.document_file) ? doc.document_file : (doc.document_file ? [doc.document_file] : []);
        if (files.length > 0) {
          return files.map(file => ({
            ...doc,
            document_file: [file]
          }));
        } else {
          return [{
            ...doc,
            document_file: []
          }];
        }
      }) || []
    };

    console.log('Processed values:', processedValues);
    console.log('Final documents:', processedValues.documents);

    if (onSubmit) {
      onSubmit(processedValues);
    }
  };

  // Handle initial document fields
  const [initialized, setInitialized] = useState(false);
  const formDocuments = Form.useWatch('documents', form) || [];

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: '#fff', padding: '40px 5vw', borderRadius: 0 }}>
      <h2>{mode === 'edit' ? 'Edit Employee' : 'Add Employee'}</h2>
      <div style={{ marginBottom: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button type="primary" htmlType="submit" form={form.getForm ? form.getForm().id : undefined} loading={loading} onClick={() => form.submit()}>
          {mode === 'edit' ? 'Update Employee' : 'Create Employee'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          ...initialValues,
          documents: initialValues?.documents?.map(doc => ({
            ...doc,
            // Ensure document_file is always an array
            document_file: Array.isArray(doc.document_file) ? doc.document_file : [doc.document_file]
          })) || [],
          leaves: initialValues?.leaves || []
        }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Card title="Personal Information" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="emp_code" label="Employee Code">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Invalid email' }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label="Phone (UAE)"
                    getValueFromEvent={(e) => e.target.value.replace(/\D/g, '')}
                    rules={[{ pattern: /^[0-9]+$/, message: 'Phone number must contain numbers only' }]}
                  >
                    <Input inputMode="numeric" placeholder="e.g. 0501234567" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="native_phone"
                    label="Native Phone (Home Country)"
                    getValueFromEvent={(e) => e.target.value.replace(/\D/g, '')}
                    rules={[{ pattern: /^[0-9]+$/, message: 'Phone number must contain numbers only' }]}
                  >
                    <Input inputMode="numeric" placeholder="e.g. 919876543210" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dob" label="Date of Birth">
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="address" label="Address">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="country" label="Country">
                    <Select showSearch optionFilterProp="children" placeholder="Select country">
                      {countryList.map(c => <Option key={c} value={c}>{c}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="profile_image"
                    label="Profile Image"
                    valuePropName="fileList"
                    getValueFromEvent={e => {
                      if (Array.isArray(e)) return e.slice(-1);
                      return e && e.fileList ? e.fileList.slice(-1) : [];
                    }}
                  >
                    <Upload
                      showUploadList={false}
                      maxCount={1}
                      beforeUpload={() => false}
                      onChange={info => {
                        const fileList = info.fileList.slice(-1);
                        if (fileList.length > 0) {
                          const file = fileList[0];
                          if (file.originFileObj) {
                            setImagePreview(URL.createObjectURL(file.originFileObj));
                          } else if (file.url) {
                            setImagePreview(file.url);
                          }
                        } else {
                          setImagePreview(null);
                        }
                      }}
                    >
                      <Button icon={<UploadOutlined />}>{imagePreview ? 'Change Image' : 'Select Image'}</Button>
                    </Upload>
                  </Form.Item>
                  {imagePreview && (
                    <div style={{ marginBottom: 16 }}>
                      <img src={imagePreview} alt="Preview" style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }} />
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Job Information" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="department_id" label="Company" rules={[{ required: true, message: 'Please select company' }]}>
                    <Select allowClear placeholder="Select company">
                      {departments.map(dep => <Option key={dep.id} value={dep.id}>{dep.name}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="skills" label="Skills">
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Select skill(s)"
                      optionFilterProp="children"
                    >
                      {skills.map(skill => (
                        <Option key={skill.id} value={skill.id}>{skill.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="status" label="Status" initialValue="active" rules={[{ required: true, message: 'Please select status' }]}>
                    <Select>
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="joining_date" label="Joining Date">
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="workmen_insurance_issue_date" label="Workmen Insurance Issue Date">
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="workmen_insurance_expiry_date" label="Workmen Insurance Expiry Date" rules={[
                    {
                      validator: (_, value) => {
                        if (value && new Date(value) < new Date(new Date().setHours(0, 0, 0, 0))) {
                          return Promise.reject(new Error('Insurance expiry date cannot be in the past'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}>
                    <Input type="date" min={new Date().toISOString().split('T')[0]} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="salary_type" label="Salary Type">
                    <Select>
                      <Option value="monthly">Monthly</Option>
                      <Option value="hourly">Hourly</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.salary_type !== currentValues.salary_type}
                >
                  {({ getFieldValue }) => {
                    const salaryType = getFieldValue('salary_type');
                    if (salaryType === 'monthly') {
                      return (
                        <>
                          <Col span={12}>
                            <Form.Item name="base_salary" label="Base Salary">
                              <Input type="number" />
                            </Form.Item>
                          </Col>
                        </>
                      );
                    }
                    if (salaryType === 'hourly') {
                      return (
                        <Col span={12}>
                          <Form.Item name="hourly_rate" label="Hourly Rate">
                            <Input type="number" />
                          </Form.Item>
                        </Col>
                      );
                    }
                    return null;
                  }}
                </Form.Item>
                <Col span={12}>
                  <Form.Item name="over_time_rate" label="Overtime Rate">
                    <Input type="number" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
        <Card title="Leave History" style={{ marginBottom: 24 }}>
          <Form.List name="leaves">
            {(fields, { add, remove }) => (
              <div key="leaves-container">
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8, flexWrap: 'wrap' }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'leave_type']}
                      label="Type"
                      rules={[{ required: true, message: 'Type' }]}
                    >
                      <Select placeholder="Type" style={{ width: 140 }}>
                        <Option value="Annual">Annual</Option>
                        <Option value="Sick">Sick</Option>
                        <Option value="Unpaid">Unpaid</Option>
                        <Option value="Emergency">Emergency</Option>
                        <Option value="Other">Other</Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'start_date']}
                      label="From"
                      rules={[{ required: true, message: 'Start' }]}
                    >
                      <Input type="date" placeholder="Start Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'end_date']}
                      label="To"
                      rules={[{ required: true, message: 'End' }]}
                    >
                      <Input type="date" placeholder="End Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'actual_return_date']}
                      label="Actual Return"
                    >
                      <Input type="date" placeholder="Actual Return Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => {
                        const prevLeave = prevValues.leaves?.[name];
                        const currLeave = currentValues.leaves?.[name];
                        return prevLeave?.end_date !== currLeave?.end_date || prevLeave?.actual_return_date !== currLeave?.actual_return_date;
                      }}
                    >
                      {({ getFieldValue }) => {
                        const leave = getFieldValue(['leaves', name]);
                        if (leave?.end_date && leave?.actual_return_date) {
                          const end = new Date(leave.end_date);
                          const actual = new Date(leave.actual_return_date);

                          // Expected return is end_date + 1 day
                          const expected = new Date(end);
                          expected.setDate(expected.getDate() + 1);

                          // Reset hours to compare dates correctly
                          expected.setHours(0, 0, 0, 0);
                          actual.setHours(0, 0, 0, 0);

                          const diffTime = actual - expected;
                          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

                          if (diffDays > 0) {
                            return <Tag color="red" style={{ marginTop: 30 }}>{diffDays} Day(s) Late</Tag>;
                          } else if (diffDays < 0) {
                            return <Tag color="green" style={{ marginTop: 30 }}>{Math.abs(diffDays)} Day(s) Early</Tag>;
                          } else {
                            return <Tag color="blue" style={{ marginTop: 30 }}>On Time</Tag>;
                          }
                        }
                        return null;
                      }}
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'reason']}
                      label="Reason"
                    >
                      <Input placeholder="Reason/Remarks" style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'form_file']}
                      label="Form"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) return e.slice(-1);
                        return e && e.fileList ? e.fileList.slice(-1) : [];
                      }}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        listType="picture"
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      >
                        <Button icon={<UploadOutlined />} size="small">File</Button>
                      </Upload>
                    </Form.Item>
                    <div style={{ marginTop: 30 }}>
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                        title="Remove Leave Record"
                      />
                    </div>
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Leave Record
                  </Button>
                </Form.Item>
              </div>
            )}
          </Form.List>
        </Card>

        <Card title="Employee Documents" style={{ marginBottom: 24 }}>
          <Form.List name="documents">
            {(fields, { add, remove }) => (
              <div key="documents-container">
                {fields.map(({ key, name, ...restField }) => {
                  const docVal = form.getFieldValue(['documents', name, 'document_file']);
                  const existingFile = Array.isArray(docVal) && docVal.length > 0 ? docVal[0] : (typeof docVal === 'string' ? docVal : null);
                  const existingUrl = existingFile ? (existingFile.url || (typeof existingFile === 'string' ? (existingFile.startsWith('http') ? existingFile : `${imagepath}${existingFile}`) : null)) : null;
                  const existingName = existingFile ? (existingFile.name || (typeof existingFile === 'string' ? existingFile.split('/').pop() : 'Document')) : null;

                  return (
                    <Row key={key} gutter={12} align="top" style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px dashed #f0f0f0' }}>
                      <Col span={5}>
                        <Form.Item
                          {...restField}
                          name={[name, 'document_type']}
                          label="Document Type"
                          rules={[{ required: true, message: 'Document type is required' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select placeholder="Select type" style={{ width: '100%' }}>
                            {documentTypes.map(dt => <Option key={dt.value} value={dt.value}>{dt.label}</Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...restField}
                          name={[name, 'issue_date']}
                          label="Issue Date"
                          getValueProps={(value) => ({
                            value: value ? new Date(value).toISOString().split('T')[0] : ''
                          })}
                          style={{ marginBottom: 0 }}
                        >
                          <Input type="date" placeholder="Issue Date" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...restField}
                          name={[name, 'expiry_date']}
                          label="Expiry Date"
                          rules={[
                            { required: true, message: 'Expiry date is required' },
                            {
                              validator: (_, value) => {
                                if (value && new Date(value) < new Date(new Date().setHours(0, 0, 0, 0))) {
                                  return Promise.reject(new Error('Expiry date cannot be in the past'));
                                }
                                return Promise.resolve();
                              }
                            }
                          ]}
                          getValueProps={(value) => ({
                            value: value ? new Date(value).toISOString().split('T')[0] : ''
                          })}
                          style={{ marginBottom: 0 }}
                        >
                          <Input type="date" placeholder="Expiry Date" style={{ width: '100%' }} min={new Date().toISOString().split('T')[0]} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'document_file']}
                          label="File"
                          valuePropName="fileList"
                          getValueProps={(value) => {
                            if (Array.isArray(value) && value.length > 0) {
                              return value.map(item => {
                                if (typeof item === 'string') {
                                  const url = item.startsWith('http') ? item : `${imagepath}${item}`;
                                  return {
                                    uid: item,
                                    name: item.split('/').pop(),
                                    status: 'done',
                                    url: url,
                                    thumbUrl: url,
                                  };
                                }
                                const fileUrl = item.url || (typeof item.document_file === 'string' ? (item.document_file.startsWith('http') ? item.document_file : `${imagepath}${item.document_file}`) : '');
                                return {
                                  ...item,
                                  url: fileUrl || item.url,
                                  thumbUrl: fileUrl || item.thumbUrl || item.url,
                                };
                              });
                            }
                            if (typeof value === 'string' && value.trim() !== '') {
                              const fileUrl = value.startsWith('http') ? value : `${imagepath}${value}`;
                              const fileName = value.split('/').pop() || 'Document';
                              return [{
                                uid: '-1',
                                name: fileName,
                                status: 'done',
                                url: fileUrl,
                                thumbUrl: fileUrl,
                              }];
                            }
                            return Array.isArray(value) ? value : [];
                          }}
                          getValueFromEvent={(e) => {
                            if (Array.isArray(e)) return e;
                            return e && e.fileList ? e.fileList : [];
                          }}
                          rules={[
                            {
                              required: mode === 'add',
                              message: 'Document file is required'
                            },
                            {
                              validator: (_, fileList) => {
                                if (!fileList || fileList.length === 0) return Promise.resolve();
                                const invalidFiles = fileList.filter(file => {
                                  const name = file.name || '';
                                  const isAllowedFormat = /\.(pdf|jpg|jpeg|png|doc|docx)$/i.test(name);
                                  const isLt5M = !file.size || file.size / 1024 / 1024 <= 5;
                                  return !isAllowedFormat || !isLt5M;
                                });
                                if (invalidFiles.length > 0) {
                                  return Promise.reject(new Error('One or more files exceed 5MB or are an unsupported format'));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Upload
                            beforeUpload={(file) => {
                              const isAllowedFormat = /\.(pdf|jpg|jpeg|png|doc|docx)$/i.test(file.name);
                              if (!isAllowedFormat) {
                                message.error(`"${file.name}" is an unsupported format. Only PDF, JPG, PNG, and DOC files are allowed.`);
                                return Upload.LIST_IGNORE;
                              }
                              const isLt5M = file.size / 1024 / 1024 <= 5;
                              if (!isLt5M) {
                                message.error(`"${file.name}" exceeds the 5MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB). Please select a file under 5MB.`);
                                return Upload.LIST_IGNORE;
                              }
                              return false;
                            }}
                            multiple={true}
                            listType="picture"
                            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.PDF,.JPG,.PNG"
                          >
                            <Button icon={<UploadOutlined />}>Choose File(s)</Button>
                          </Upload>
                        </Form.Item>
                        <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                          Allowed formats: PDF, JPG, PNG (Max 5MB per file)
                        </div>
                        {existingUrl && (() => {
                          const isImageFile = /\.(jpg|jpeg|png|gif|webp)$/i.test(existingName || existingUrl);
                          return (
                            <div style={{ marginTop: 8 }}>
                              <a
                                href={existingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justify: 'space-between',
                                  width: '100%',
                                  padding: '6px 10px',
                                  border: '1px solid #d9d9d9',
                                  borderRadius: 6,
                                  backgroundColor: '#ffffff',
                                  color: '#1890ff',
                                  fontSize: 13,
                                  textDecoration: 'none',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
                                  {isImageFile ? (
                                    <img
                                      src={existingUrl}
                                      alt={existingName}
                                      style={{
                                        width: 36,
                                        height: 36,
                                        objectFit: 'cover',
                                        borderRadius: 4,
                                        border: '1px solid #e8e8e8',
                                        flexShrink: 0
                                      }}
                                    />
                                  ) : (
                                    <FileOutlined style={{ fontSize: 24, color: '#1890ff', flexShrink: 0 }} />
                                  )}
                                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 400 }}>
                                    {existingName}
                                  </span>
                                </div>
                                <EyeOutlined style={{ color: '#8c8c8c', marginLeft: 8, flexShrink: 0, fontSize: 16 }} />
                              </a>
                            </div>
                          );
                        })()}
                      </Col>
                      <Col span={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 30 }}>
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', fontSize: 18, cursor: 'pointer' }} />
                      </Col>
                    </Row>
                  );
                })}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Document
                  </Button>
                </Form.Item>
              </div>
            )}
          </Form.List>
        </Card>
      </Form>
    </div>
  );
};

export default EmployeeForm; 